import { useEffect, useState } from 'react';

import posthog from 'posthog-js';

import { useEmail } from '@/components/providers/EmailProvider/EmailProvider';
import useAuth from '@/hooks/useAuth';
import useMarkOnboardingCompleted from '@/mutations/useMarkOnboardingCompleted';

import useTargetRect from '../hooks/useTargetRect';
import STEPS from '../steps';

import Spotlight from './Spotlight';
import Tooltip from './Tooltip';

export default function OnboardingTour() {
  const { me } = useAuth();
  const [currentStepIndex, setCurrentStepIndex] = useState<
    number | undefined
  >();

  const currentStep =
    currentStepIndex !== undefined ? STEPS[currentStepIndex] : undefined;

  const { analysis, party } = useEmail();
  const isActive =
    !me?.metadata?.outlookSidebarOnboardingCompletedAt &&
    party.status === 'Selected' &&
    analysis.analysis?.summary_bullets !== undefined &&
    !analysis.isLoading;

  const targetRect = useTargetRect(currentStep?.target);

  const { mutateAsync: markOnboardingCompleted } = useMarkOnboardingCompleted();

  const handleClose = () => {
    setCurrentStepIndex(undefined);
    markOnboardingCompleted();
  };

  const handleNext = () => {
    const nextIndex = (currentStepIndex ?? 0) + 1;

    if (nextIndex === STEPS.length) {
      posthog.capture('Completed onboarding');
      handleClose();

      return;
    }

    setCurrentStepIndex(nextIndex);
  };

  const handlePrevious = () => setCurrentStepIndex((currentStepIndex ?? 0) - 1);

  useEffect(() => {
    if (!isActive) return;

    const timeoutId = setTimeout(() => setCurrentStepIndex(0), 1_000);

    return () => clearTimeout(timeoutId);
  }, [analysis.actions, isActive]);

  if (currentStepIndex === undefined || !currentStep || !targetRect)
    return null;

  return (
    <>
      <Spotlight rect={targetRect} />

      <Tooltip
        step={currentStep}
        rect={targetRect}
        currentIndex={currentStepIndex}
        totalSteps={STEPS.length}
        handleNext={handleNext}
        handlePrevious={handlePrevious}
        handleClose={handleClose}
      />
    </>
  );
}
